import { type SubmitEvent, type KeyboardEvent, useId } from "react";
import { type FieldValues } from "react-hook-form";

import { useAsyncSubmit } from "ui/button/loading-button/use-async-submit";
import { shouldConfirmOnEnter } from "ui/dialog/dialog.helpers";

import { type DialogActionsType } from "ui/dialog/dialog-actions/dialog-actions.types";
import { type FormDialogProps } from "ui/form-dialog/form-dialog.types";

type UseFormDialogOptions<TValues extends FieldValues, TSubmitValues> = Pick<
  FormDialogProps<TValues, TSubmitValues>,
  "form" | "onSubmit" | "step"
> & {
  submitDisabled: boolean;
  submitting: boolean;
  submitOnEnter: boolean;
};

export type FormDialogState = {
  formId: string;
  /** A wizard — `step` set — gets Figma's `steps` footer; anything else the `generic` one. */
  actionsType: DialogActionsType;
  isPending: boolean;
  isSubmitBlocked: boolean;
  handleSubmit: (event: SubmitEvent<HTMLFormElement>) => void;
  handleKeyDown: (event: KeyboardEvent<HTMLFormElement>) => void;
};

export const useFormDialog = <TValues extends FieldValues, TSubmitValues = TValues>({
  form,
  onSubmit,
  submitDisabled,
  submitting,
  submitOnEnter,
  step,
}: UseFormDialogOptions<TValues, TSubmitValues>): FormDialogState => {
  const formId = useId();

  const { submit, isSubmitting: isAsyncSubmitting } = useAsyncSubmit(
    async (values: TSubmitValues) => {
      await onSubmit(values);
    },
    { onError: (error) => console.error("Form dialog submit failed:", error) },
  );

  const isPending = submitting || isAsyncSubmitting || form.formState.isSubmitting;
  const hasErrors = Object.keys(form.formState.errors).length > 0;
  const isSubmitBlocked = submitDisabled || hasErrors || isPending;

  const submitValidated = form.handleSubmit((values) => submit(values).catch(() => undefined));

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.stopPropagation();
    void submitValidated(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    const isInputEnter = event.key === "Enter" && event.target instanceof HTMLInputElement;

    if (!isInputEnter || event.defaultPrevented) {
      return;
    }

    const shouldSubmit = shouldConfirmOnEnter(event, {
      enabled: submitOnEnter,
      blocked: isSubmitBlocked,
    });

    event.preventDefault();

    if (shouldSubmit) {
      event.currentTarget.requestSubmit();
    }
  };

  const actionsType: DialogActionsType = step ? "steps" : "generic";

  return { formId, actionsType, isPending, isSubmitBlocked, handleSubmit, handleKeyDown };
};
